/**
 * Biometrics Service
 *
 * Handles face comparison, liveness detection, and verification.
 */

import { getSharedClient } from './api';

const client = getSharedClient();

export const BiometricsService = {
  // Face comparison
  async compareFaces(sourceImage, targetImage, threshold = 90.0) {
    return client.post('/biometrics/compare', {
      source_image: sourceImage,
      target_image: targetImage,
      similarity_threshold: threshold,
    });
  },

  // Liveness detection
  async detectLiveness(image, sessionId = null) {
    return client.post('/biometrics/liveness', {
      image,
      session_id: sessionId,
    });
  },

  // Face detection
  async detectFaces(image) {
    return client.post('/biometrics/detect', { image });
  },

  // Full applicant verification (base64)
  async verifyApplicant(applicantId, selfieImage, checkLiveness = true) {
    const formData = new FormData();
    formData.append('selfie_image', selfieImage);
    formData.append('check_liveness', checkLiveness.toString());

    // MUST use postForm - post() JSON.stringifies FormData into "{}" (-> 422).
    // Don't set Content-Type manually; the browser adds the multipart boundary.
    return client.postForm(`/biometrics/verify/${applicantId}`, formData);
  },

  // Full applicant verification (file upload)
  async verifyApplicantWithFile(applicantId, selfieFile, checkLiveness = true) {
    const formData = new FormData();
    formData.append('selfie', selfieFile);
    formData.append('check_liveness', checkLiveness.toString());

    // MUST use postForm - post() JSON.stringifies FormData into "{}" (-> 422).
    // Don't set Content-Type manually; the browser adds the multipart boundary.
    return client.postForm(`/biometrics/verify-upload/${applicantId}`, formData);
  },

  // Service status
  async getStatus() {
    return client.get('/biometrics/status');
  },

  // ===========================================
  // Face Liveness (Interactive)
  // ===========================================

  // Create a new liveness session
  async createLivenessSession(applicantId, challengePreference = 'PREFER_COMPOSITE_CHALLENGES') {
    return client.post('/biometrics/liveness/sessions', {
      applicant_id: applicantId,
      challenge_preference: challengePreference,
    });
  },

  // Get liveness session status
  async getLivenessSession(sessionId) {
    return client.get(`/biometrics/liveness/sessions/${sessionId}`);
  },

  // Get liveness session results (fetches from AWS)
  async getLivenessSessionResults(sessionId) {
    return client.get(`/biometrics/liveness/sessions/${sessionId}/results`);
  },

  // Compare liveness reference to ID document
  async matchLivenessToId(sessionId) {
    return client.post(`/biometrics/liveness/sessions/${sessionId}/face-match`);
  },

  // List applicant's liveness sessions
  async listApplicantLivenessSessions(applicantId, limit = 10) {
    return client.get(`/biometrics/liveness/applicant/${applicantId}/sessions`, {
      params: { limit },
    });
  },
};

export default BiometricsService;
